
import {
    useEffect,
    useRef,
    useState
} from 'react';
import { Rnd } from 'react-rnd';

import "cropperjs/dist/cropper.css";
import Cropper from "react-cropper";

import process_vars from '../../intermediate/process_vars';
import Modal from '../../utils/Modal';
import execute_script from './execute_script';

const boxShadowRadius = 15;

// Upper bound on the saved image's pixel size. Snapshots travel to the backend
// inside the record's JSON as a base64 data URL, so a full-resolution webcam
// frame would bloat every save and reload of the record.
const MAX_SAVED_SIZE = 1600;

// Crop ratio taken from the element's configured aspect ratio. The properties
// panel writes these as strings, and cropperjs reads NaN as "no fixed ratio",
// which is the right fallback for a malformed value.
function cropAspectRatioOf(comps, id) {
    let horizontal = parseFloat(comps[id].aspectRatio ? comps[id].aspectRatio[0] : NaN);
    let vertical = parseFloat(comps[id].aspectRatio ? comps[id].aspectRatio[1] : NaN);
    if (isNaN(horizontal) || isNaN(vertical) || horizontal <= 0 || vertical <= 0) {
        return NaN;
    }
    return horizontal / vertical;
}

export default function Snapshot({ id, comps, setter, vw, vars, setVars, editable, pdf, externals, setExternals, recordName, setRecordName }) {

    const [isCapturingSnapshot, setIsCapturingSnapshot] = useState(false);

    // The PDF/export path renders without a setter, so treat it as read-only.
    const canCapture = editable && !comps[id].disable && typeof setter === 'function';

    let boxShadow = "0px 0px 0px #ff4f42";

    if (comps[id].error) {
        boxShadow = `0px 0px ${boxShadowRadius}px #ff4f42`;
    }
    else if (comps[id].hasOwnProperty('warning')) {
        if (comps[id].warning) {
            boxShadow = `0px 0px ${boxShadowRadius}px #d35400`;
        }
    }
    else if (comps[id].disable) {
        boxShadow = `0px 0px ${boxShadowRadius}px #4e6df5`;
    }

    const placeholder = (
        <>
            <i className='fa fa-solid fa-camera' style={{
                color: 'black',
                fontSize: '1.5vmax',
            }}></i>
            <p style={{
                width: '100%',
                textAlign: 'center',
                fontWeight: '300',
                color: 'black'
            }}>
                Capture Snapshot
            </p>
        </>
    );

    let data_source = '';
    if (comps[id].data.length > 0) {
        data_source = comps[id].data;
    } else {
        data_source = comps[id].image_id;
    }

    // The record payload carries the snapshot as a data URL on `data`; the backend
    // turns it into a stored image and hands back an image_id on reload.
    const saveSnapshot = (dataUrl) => {
        let new_comps = [...comps];
        new_comps[id].data = dataUrl;
        setter(new_comps);
        execute_script(1, id, comps, setter, vars, setVars, externals, setExternals, recordName, setRecordName);
        setIsCapturingSnapshot(false);
    };

    return (
        <>
            <div>
                {
                    pdf == true ? (
                        <div style={{
                            border: process_vars(comps[id].props.borderEnabled, vars) ?
                                `${process_vars(comps[id].props.borderColor, vars)} solid ${process_vars(comps[id].props.borderWidth)}pt` : '#000 solid 0px',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: process_vars(comps[id].size[0], vars) * vw,
                            height: process_vars(comps[id].size[1], vars) * vw,
                            transform: `translate(${process_vars(comps[id].position[0], vars) * vw}px, ${process_vars(comps[id].position[1], vars) * vw}px)`

                        }}>
                            {data_source ? <img style={{ width: "100%" }} src={data_source} alt="snapshot" /> : placeholder}
                        </div>
                    ) : (
                        <Rnd className='comp' style={{
                            backgroundColor: process_vars(comps[id].props.backgroundColor, vars),
                            textAlign: 'center',
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: 'hidden',
                            zIndex: '0',
                            border: process_vars(comps[id].props.borderEnabled, vars) ?
                                `${process_vars(comps[id].props.borderColor, vars)} solid ${process_vars(comps[id].props.borderWidth)}pt` : '#000 solid 0px',
                            position: 'absolute',
                            boxShadow: editable ? boxShadow : 'none',
                            transition: 'box-shadow 0.5s'
                        }} position={{
                            x: process_vars(comps[id].position[0], vars) * vw,
                            y: process_vars(comps[id].position[1], vars) * vw
                        }} size={{
                            width: process_vars(comps[id].size[0], vars) * vw,
                            height: process_vars(comps[id].size[1], vars) * vw
                        }} minWidth={window.innerWidth * 0.05} minHeight={window.innerWidth * 0.025}
                            aspectRatio={cropAspectRatioOf(comps, id)}
                            disableDragging={true}
                            enableResizing={false}
                            lockAspectRatio={true}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}>
                            <div className={canCapture ? 'pointy' : ''} style={{
                                textAlign: 'center',
                                width: '100%',
                            }} onClick={(e) => {
                                if (canCapture) {
                                    setIsCapturingSnapshot(true);
                                }
                            }}>
                                {data_source ? <img style={{ width: "100%" }} src={data_source} alt="snapshot" /> : placeholder}
                            </div>
                        </Rnd>
                    )
                }
            </div >
            {
                isCapturingSnapshot ?
                    <Modal width='60' height='75'>
                        <SnapshotEditor
                            aspectRatio={cropAspectRatioOf(comps, id)}
                            onSave={saveSnapshot}
                            onExit={() => setIsCapturingSnapshot(false)}
                        />
                    </Modal >
                    : <></>
            }
        </>
    );

}

// Turns a getUserMedia rejection into something a form user can act on.
function cameraErrorMessage(error) {
    switch (error && error.name) {
        case 'NotAllowedError':
        case 'SecurityError':
            return 'Camera access was blocked. Allow camera access for this site, then try again.';
        case 'NotFoundError':
        case 'DevicesNotFoundError':
            return 'No camera was found. Connect a webcam, or upload an image file instead.';
        case 'NotReadableError':
        case 'TrackStartError':
            return 'The camera is already in use by another application.';
        case 'OverconstrainedError':
            return 'The selected camera is no longer available. Pick another one.';
        default:
            return (error && error.message) ? error.message : 'The camera could not be started.';
    }
}

// Two-step capture: pick a source (webcam or file), then crop it to the element's
// aspect ratio. `mode` is 'camera' while a source is being chosen and 'crop' once
// there is an image to work on.
function SnapshotEditor({ aspectRatio, onSave, onExit }) {

    const [mode, setMode] = useState('camera');
    const [image, setImage] = useState('');
    const [devices, setDevices] = useState([]);
    const [deviceId, setDeviceId] = useState('');
    const [cameraError, setCameraError] = useState('');

    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const cropperRef = useRef(null);

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
    };

    // Opens the selected camera (or the default one) and, once permission has been
    // granted, lists every connected video input — device labels stay empty until
    // then, so enumerating any earlier would show unnamed entries.
    useEffect(() => {
        if (mode !== 'camera') {
            stopCamera();
            return undefined;
        }

        let cancelled = false;

        (async () => {
            try {
                if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                    throw new Error('This browser cannot open a camera here. Pages served over plain HTTP are blocked from using one — upload an image file instead.');
                }

                stopCamera();
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: deviceId ? { deviceId: { exact: deviceId } } : true,
                    audio: false
                });

                if (cancelled) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }

                streamRef.current = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    // Safari rejects play() if the element is not ready yet; the
                    // autoPlay attribute still starts the preview in that case.
                    const played = videoRef.current.play();
                    if (played && played.catch) {
                        played.catch(() => { });
                    }
                }
                setCameraError('');

                const connected = await navigator.mediaDevices.enumerateDevices();
                if (!cancelled) {
                    setDevices(connected.filter((device) => device.kind === 'videoinput'));
                }
            } catch (error) {
                if (!cancelled) {
                    setCameraError(cameraErrorMessage(error));
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [mode, deviceId]);

    // Release the camera when the editor closes — otherwise the capture light
    // stays on after the modal is gone.
    useEffect(() => stopCamera, []);

    const captureFrame = (e) => {
        e.preventDefault();
        const video = videoRef.current;
        if (!video || !video.videoWidth) return;

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);

        setImage(canvas.toDataURL('image/png'));
        setMode('crop');
        stopCamera();
    };

    const chooseFile = (e) => {
        e.preventDefault();
        let files;
        if (e.dataTransfer) {
            files = e.dataTransfer.files;
        } else if (e.target) {
            files = e.target.files;
        }
        if (!files || files.length === 0) return;

        const reader = new FileReader();
        reader.onload = () => {
            setImage(reader.result);
            setMode('crop');
            stopCamera();
        };
        reader.readAsDataURL(files[0]);

        // Clearing the input lets the same file be picked again after a retake —
        // otherwise the value is unchanged and no change event fires.
        if (e.target) {
            e.target.value = '';
        }
    };

    const retake = (e) => {
        e.preventDefault();
        setImage('');
        setMode('camera');
    };

    const save = (e) => {
        e.preventDefault();
        const cropper = cropperRef.current ? cropperRef.current.cropper : undefined;
        if (typeof cropper === "undefined") return;

        const cropped = cropper.getCroppedCanvas({
            maxWidth: MAX_SAVED_SIZE,
            maxHeight: MAX_SAVED_SIZE,
            imageSmoothingQuality: 'high'
        });
        if (!cropped) return;

        onSave(cropped.toDataURL('image/png'));
    };

    return (
        <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: '2vh 2vh 0 2vh',
            boxSizing: 'border-box'
        }}>
            <p style={{
                fontSize: '1.2vmax',
                fontWeight: '300',
                marginBottom: '1.5vh'
            }}>{mode === 'camera' ? 'Capture Snapshot' : 'Crop Snapshot'}</p>

            {mode === 'camera' ? (
                <>
                    <div style={{
                        width: '100%',
                        height: '42vh',
                        backgroundColor: '#111111',
                        border: '1px #FAF3DD solid',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                    }}>
                        {cameraError ? (
                            <p style={{
                                fontSize: '0.9vmax',
                                fontWeight: '300',
                                width: '80%'
                            }}>{cameraError}</p>
                        ) : (
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%'
                                }}
                            />
                        )}
                    </div>

                    <div style={{
                        marginTop: '1.5vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        gap: '1vmax'
                    }}>
                        {devices.length > 0 ? (
                            <select className='panel-dropdown' value={deviceId} onChange={(e) => {
                                setCameraError('');
                                setDeviceId(e.target.value);
                            }} style={{
                                fontSize: '0.9vmax',
                                maxWidth: '20vw'
                            }}>
                                <option value=''>Default Camera</option>
                                {devices.map((device, k) => (
                                    <option key={device.deviceId || k} value={device.deviceId}>
                                        {device.label || `Camera ${k + 1}`}
                                    </option>
                                ))}
                            </select>
                        ) : <></>}
                        <label className='panel-button' style={{
                            marginBottom: '0'
                        }}>
                            Upload File
                            <input type="file" accept="image/*" onChange={chooseFile} style={{
                                display: 'none'
                            }} />
                        </label>
                    </div>
                </>
            ) : (
                <div style={{
                    width: '100%'
                }}>
                    <Cropper
                        ref={cropperRef}
                        style={{ height: '45vh', width: "100%" }}
                        aspectRatio={aspectRatio}
                        src={image}
                        viewMode={1}
                        minCropBoxHeight={10}
                        minCropBoxWidth={10}
                        background={false}
                        responsive={true}
                        autoCropArea={1}
                        checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
                        guides={true}
                    />
                </div>
            )}

            <div style={{
                marginTop: 'auto',
                paddingBottom: '1vmax',
                display: 'flex',
                justifyContent: 'center',
                gap: '0.75vmax'
            }}>
                {mode === 'camera' ? (
                    <a href='' className='panel-button' style={{
                        opacity: cameraError ? '0.4' : '1'
                    }} onClick={(e) => {
                        if (cameraError) {
                            e.preventDefault();
                            return;
                        }
                        captureFrame(e);
                    }}>Capture</a>
                ) : (
                    <>
                        <a href='' className='panel-button' onClick={retake}>Retake</a>
                        <a href='' className='panel-button' onClick={save}>Save</a>
                    </>
                )}
                <a href='' className='panel-button' onClick={(e) => {
                    e.preventDefault();
                    stopCamera();
                    onExit();
                }}>Exit</a>
            </div>
        </div>
    );

}
